import { PretaxPlan } from 'apis/pretaxCompanyConfiguration';
import {
  PRETAX_ACCOUNT_TYPES,
  PRETAX_ACCOUNT_NAMES,
  PRETAX_ACCOUNT_STATUS,
  TWIC_PRETAX_STORES_CONSTANTS,
  FSA_STORE_PRETAX_ACCOUNT_TYPES,
  ELIGIBLE_PRETAX_ACCOUNT_TYPE,
  PRETAX_COMMUTER_ENROLL_STATUS,
  PRETAX_COMMUTER_ACCOUNT_TYPES,
  PRETAC_HSA_ACCOUNT_TYPES,
} from 'constants/accounts';
import moment from 'moment';

export const getAccountName = (
  account_type: string,
  account_name: string,
  account_info_type?: string
) => {
  switch (account_type.toLowerCase()) {
    case PRETAX_ACCOUNT_TYPES.TRANSIT:
      return `${PRETAX_ACCOUNT_NAMES.TRANSIT}${
        account_info_type === 'TRP' ? ' Post-Tax' : ''
      }`;
    case PRETAX_ACCOUNT_TYPES.PARKING:
      return `${PRETAX_ACCOUNT_NAMES.PARKING}${
        account_info_type === 'PKP' ? ' Post-Tax' : ''
      }`;
    case PRETAX_ACCOUNT_TYPES.OTHER:
      return account_name;
    default:
      return account_name;
  }
};

export const getExternalStoreLink = ({
  store_name,
}: {
  store_name: string;
}) => {
  switch (store_name) {
    case TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.AMAZON.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.CVS.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.LINK;
    default:
      return TWIC_PRETAX_STORES_CONSTANTS.AMAZON.LINK;
  }
};

export const getPretaxAccountStores = ({
  pretax_account,
}: {
  pretax_account: PRETAX_ACCOUNT_TYPES;
}) => {
  const external_stores: { [key in PRETAX_ACCOUNT_TYPES]?: string[] } = {
    [PRETAX_ACCOUNT_TYPES.FSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PRETAX_ACCOUNT_TYPES.LPF]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PRETAX_ACCOUNT_TYPES.HSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PRETAX_ACCOUNT_TYPES.WCS]: [
      TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PRETAX_ACCOUNT_TYPES.LPFSAHSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
  };
  return external_stores[pretax_account];
};

export const filterActivePretaxAccountsWithFsaType = <
  T extends { account_status_code: string; account_type: string }
>({
  pretax_accounts,
}: {
  pretax_accounts: Array<T>;
}) =>
  pretax_accounts
    .filter(
      ({ account_status_code }) =>
        account_status_code === PRETAX_ACCOUNT_STATUS.ACTIVE
    )
    .filter(({ account_type }) =>
      Object.values(FSA_STORE_PRETAX_ACCOUNT_TYPES).includes(
        account_type as FSA_STORE_PRETAX_ACCOUNT_TYPES
      )
    );

export const excludeTerminatedAccountsForTerminatedEmployees = <
  T extends { account_status_code: string }
>({
  pretax_accounts,
  is_terminated: is_employee_terminated,
}: {
  pretax_accounts: Array<T>;
  is_terminated: boolean;
}) =>
  pretax_accounts.filter(({ account_status_code }) => {
    const is_account_terminated =
      account_status_code === PRETAX_ACCOUNT_STATUS.TERMINATED;
    return !(is_account_terminated && is_employee_terminated);
  });

export const getPretaxAccountLabel = ({
  account_type,
}: {
  account_type: PRETAX_ACCOUNT_TYPES | string;
}) => {
  switch (account_type.toLowerCase()) {
    case PRETAX_ACCOUNT_TYPES.WCS:
      return PRETAX_ACCOUNT_NAMES.WCS;
    case PRETAX_ACCOUNT_TYPES.LPF:
      return PRETAX_ACCOUNT_NAMES.LPF;
    default:
      return account_type;
  }
};

export const resolveNextEnrollWindow = ({
  plan_start_date,
}: {
  plan_start_date: number;
}) => {
  const base_date = moment.max(
    moment.utc(),
    moment.utc(plan_start_date).subtract(1, 'month')
  );
  const next_month_first = moment
    .utc(base_date)
    .add(1, 'month')
    .startOf('month')
    .valueOf();
  const enroll_start =
    base_date.date() > 15
      ? moment.utc(next_month_first).add(1, 'month').valueOf()
      : next_month_first;
  const enroll_deadline = moment
    .utc(enroll_start)
    .subtract(1, 'month')
    .date(15)
    .valueOf();

  return {
    enroll_start,
    enroll_deadline,
  };
};

export const getEligibleCommuterPlans = ({
  pretax_plans,
  plan_year,
  enroll_status,
}: {
  pretax_plans: PretaxPlan[];
  plan_year: string | null | undefined;
  enroll_status: string;
}) =>
  /**
   * TODO: Move to endless single plan once we have in-house pretax plan so we don't need plan_year anymore.
   *
   * This function returns eligible commuter plans for current user and company
   * If enroll_status is pending, we will return all commuters plans by their year reversely
   * Else return plans exactly match by the year
   */
  pretax_plans
    .filter((p) => {
      if (ELIGIBLE_PRETAX_ACCOUNT_TYPE.indexOf(p.twic_plan_type) === -1) {
        return false;
      }

      if (enroll_status === PRETAX_COMMUTER_ENROLL_STATUS.SELECTION_PENDING) {
        return true;
      } else if (
        plan_year &&
        typeof plan_year === 'string' &&
        Number.parseInt(plan_year, 10) === moment.utc(p.start_date).year()
      ) {
        return true;
      }

      return false;
    })
    .sort((a, b) => (moment(a.start_date) > moment(b.start_date) ? -1 : 1));

export const isFSATypeAccount = ({
  account_type,
  account_info_type,
}: {
  account_type: string;
  account_info_type: string;
}) => {
  if (
    Object.values([
      PRETAX_ACCOUNT_TYPES.FSA,
      PRETAX_ACCOUNT_TYPES.LPFSA,
      PRETAX_ACCOUNT_TYPES.LPFSAHSA,
      PRETAX_ACCOUNT_TYPES.LPF,
    ]).some((search) => search === account_type.toLowerCase())
  ) {
    // FSA type accounts
    return true;
  }

  if (
    account_type.toLowerCase() === PRETAX_ACCOUNT_TYPES.OTHER &&
    (account_info_type === 'DCA' || account_info_type === 'LFS')
  ) {
    // DCFSA or LFS
    return true;
  }

  return false;
};

export const isCommuterTypeAccount = (account_type: string) =>
  PRETAX_COMMUTER_ACCOUNT_TYPES.includes(account_type);

export const isHSATypeAccount = (account_type: string) =>
  PRETAC_HSA_ACCOUNT_TYPES.includes(account_type);

export const getAccountDescription = ({
  account_type,
  account_info_type,
}: {
  account_type: string;
  account_info_type: string;
}) => {
  if (isFSATypeAccount({ account_type, account_info_type })) {
    return 'Use pretax funds to pay for dental, vision, and and post-deductible medical expenses, for you and your dependents.';
  } else if (isCommuterTypeAccount(account_type)) {
    return 'Use pretax funds to pay for transit expenses while traveling to and from work.';
  } else if (isHSATypeAccount(account_type)) {
    return 'Use pretax funds to pay for medical expenses for you and your dependents.';
  }

  return '';
};
